/**
 * SidebarItem — a single leaf row in the sidebar tree.
 * Renders as a TanStack <Link>; active state via aria-current="page" + green tint.
 * Depth-aware indentation.
 */
import { Link } from "@tanstack/react-router";
import type { SidebarLeaf } from "../../lib/sidebar-tree";

export interface SidebarItemProps {
	leaf: SidebarLeaf;
	depth: number; // 0 = root, 1 = subsection, 2 = sub-sub-section
	active: boolean;
	onFocus?: () => void;
	tabIndex?: number;
}

export function SidebarItem({
	leaf,
	depth,
	active,
	onFocus,
	tabIndex,
}: SidebarItemProps) {
	// Indent: 12px base + 16px per depth level.
	const pl = 12 + depth * 16;
	return (
		<li role="none">
			<Link
				to={leaf.href}
				role="treeitem"
				aria-current={active ? "page" : undefined}
				aria-level={depth + 2}
				data-slug={leaf.slug}
				tabIndex={tabIndex}
				onFocus={onFocus}
				className={
					active
						? "flex items-center gap-2 py-1.5 pr-3 text-[13px] text-green-700 bg-green-50 border-l-2 border-green-600 font-medium"
						: "flex items-center gap-2 py-1.5 pr-3 text-[13px] text-zinc-700 border-l-2 border-transparent hover:bg-zinc-50 hover:text-zinc-900"
				}
				style={{ paddingLeft: pl }}
			>
				<span className="truncate">{leaf.label}</span>
			</Link>
		</li>
	);
}
